import { UAAppResources, UAApps } from '@unifyapps/defs/types/apps';
import { StandardDataSourceIds } from '@unifyapps/defs/types/dataSourceIds';
import type { UAModules } from '@unifyapps/defs/types/modules';
import type { Entity } from '@unifyapps/network/generated/models/entity';
import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import { Op } from '@unifyapps/network/generated/models/op';
import getEnabledItems from '../../utils/getEnabledItems';

export interface GetUserPreferencePayload {
  triggerInputCondition: Record<string, unknown>;
  page?: {
    offset?: number;
    limit?: number;
  };
}

export interface SetUserPreferencePayload {
  applicationId: string;
  module: UAModules;
  recordId: string;
  key: string;
  value: string;
}

export type UserPreferenceEntity = Entity & {
  properties: SetUserPreferencePayload;
  id: string;
};

export const buildGetUserPreferencePayloadRequest = ({
  triggerInputCondition,
  page,
}: GetUserPreferencePayload): ExecuteWorkflowNodeRequest => {
  return {
    context: {
      appName: UAApps.StandardEntities,
      resourceName: UAAppResources.StandardEntitiesFetchUserPreferences,
    },
    // Cannot use same dataSourceId for buildGetUserPreferencePayloadRequest and
    // buildSetUserPreferencePayloadRequest since validation of dataSource would
    // fail in matrix app
    id: StandardDataSourceIds.FetchUserPreferences,
    inputs: {
      triggerInputCondition,
      page,
    },
  };
};

export const buildSetUserPreferencePayloadRequest = ({
  applicationId,
  module,
  recordId,
  key,
  value,
}: SetUserPreferencePayload): ExecuteWorkflowNodeRequest => {
  return {
    context: {
      appName: UAApps.StandardEntities,
      resourceName: UAAppResources.StandardEntitiesCreateOrUpdateUserPreferences,
    },
    id: StandardDataSourceIds.SetUserPreferences,
    inputs: {
      record: {
        applicationId,
        module,
        recordId,
        key,
        value,
      },
    },
  };
};

export const getUserPreferenceTriggerInputCondition = ({
  applicationId,
  module,
  recordId,
  key,
}: {
  applicationId: string;
  module: UAModules;
  key: string | string[];
  recordId?: string;
}) => {
  return {
    filters: getEnabledItems([
      {
        property: 'properties_applicationId',
        filter: { operator: Op.EQUAL, value: applicationId },
      },
      {
        property: 'properties_key',
        filter: { operator: Op.IN, value: Array.isArray(key) ? key : [key] },
      },
      {
        property: 'properties_module',
        filter: { operator: Op.EQUAL, value: module },
      },
      {
        property: 'properties_recordId',
        filter: { operator: Op.EQUAL, value: recordId },
        enabled: Boolean(recordId),
      },
    ]),
    operator: Op.AND,
  };
};
